import * as Location from 'expo-location';
import { useEffect, useState } from 'react';

import { forwardGeocode } from '@/services/geocodeService';

/**
 * Distância em linha reta do cliente até o prestador (km), formatada em pt-BR.
 *
 * Destino: usa a coordenada salva quando existe; senão, geocodifica o endereço
 * em texto (Rua, número, bairro, cidade). Assim o prestador que só escreveu o
 * endereço, sem fixar pino no mapa, ainda mostra distância.
 *
 * Silencioso de propósito: pede permissão sem forçar; se o cliente negar, o
 * destino não resolve, ou a leitura falha, devolve `null` e a UI só esconde a
 * distância. Nunca vira erro na tela.
 */
export function useDistanceTo(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  address?: string | null,
): string | null {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLabel(null);

    (async () => {
      try {
        const target = await resolveTarget(latitude, longitude, address);
        if (!alive || !target) return;

        const permission = await Location.getForegroundPermissionsAsync();
        const granted =
          permission.granted ||
          (permission.canAskAgain &&
            (await Location.requestForegroundPermissionsAsync()).granted);
        if (!alive || !granted) return;

        const position = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });
        if (!alive) return;

        const km = haversineKm(
          position.coords.latitude,
          position.coords.longitude,
          target.latitude,
          target.longitude,
        );
        setLabel(formatDistance(km));
      } catch {
        // localização indisponível: sem distância, sem barulho.
      }
    })();

    return () => {
      alive = false;
    };
  }, [latitude, longitude, address]);

  return label;
}

async function resolveTarget(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  address: string | null | undefined,
): Promise<{ latitude: number; longitude: number } | null> {
  if (latitude != null && longitude != null) return { latitude, longitude };
  if (address?.trim()) return forwardGeocode(address);
  return null;
}

function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** "800 m" até 1 km; "2,4 km" abaixo de 10; "18 km" acima. */
function formatDistance(km: number): string {
  if (km < 1) return `${Math.max(50, Math.round((km * 1000) / 50) * 50)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
}
